import React from "react";

const FreelancerTemplate = ({ data = {} }) => {
  const safe = (v, d = "") => (v !== undefined && v !== null ? v : d);

  const invoice = {
    companyName: safe(data.companyName, "Metagrad Labs Pvt. Ltd."),
    invoiceNumber: safe(data.invoiceNumber, "INV-2025-001"),
    date: safe(data.date, "15/01/2026"),
    customerId: safe(data.customerId, "CUST-1001"),

    companyDetails: {
      address: safe(data?.companyDetails?.address, "3rd Floor, Technopark, Andheri East"),
      city: safe(data?.companyDetails?.city, "Mumbai, Maharashtra"),
      pincode: safe(data?.companyDetails?.pincode, "400069"),
      phone: safe(data?.companyDetails?.phone, "+91 98765 43210"),
      email: safe(data?.companyDetails?.email, "billing@metagradlabs.com"),
      website: safe(data?.companyDetails?.website, "www.metagradlabs.com"),
    },

    client: {
      name: safe(data?.client?.name, "ABC Enterprises"),
      address: safe(data?.client?.address, "2nd Floor, Business Tower, Connaught Place"),
      city: safe(data?.client?.city, "New Delhi"),
      pincode: safe(data?.client?.pincode, "110001"),
      phone: safe(data?.client?.phone, "+91 91234 56789"),
    },

    items: Array.isArray(data?.items) && data.items.length
      ? data.items
      : [
          { desc: "Website Development", unitCost: 25000, qty: 1 },
          { desc: "UI/UX Design", unitCost: 12000, qty: 1 },
          { desc: "Hosting & Deployment", unitCost: 3000, qty: 1 },
        ],

    discount: Number(data?.discount || 0),
    taxRate: Number(data?.taxRate || 18),
  };

  const primaryGreen = "bg-[#22C55E]";
  const textGreen = "text-[#22C55E]";

  const calculatedItems = invoice.items.map((item) => {
    const qty = Number(item?.qty || 0);
    const rate = Number(item?.unitCost || 0);
    return {
      desc: safe(item?.desc, ""),
      unitCost: rate,
      qty,
      amount: (qty * rate).toFixed(2),
    };
  });

  const subtotal = calculatedItems.reduce((sum, i) => sum + Number(i.amount || 0), 0);
  const tax = ((subtotal - invoice.discount) * invoice.taxRate) / 100;
  const total = subtotal - invoice.discount + tax;

  return (
    <div id="freelancer-invoice" className="bg-white p-6 text-gray-700 mx-auto text-sm">

      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className={`text-3xl font-bold ${textGreen} mb-2`}>
            {invoice.companyName}
          </h1>
          <div className="space-y-0.5 text-gray-500 text-xs">
            <div>{invoice.companyDetails.address}</div>
            <div>{invoice.companyDetails.city} - {invoice.companyDetails.pincode}</div>
            <div>{invoice.companyDetails.phone}</div>
            <div>{invoice.companyDetails.email}</div>
            <div>{invoice.companyDetails.website}</div>
          </div>
        </div>

        <div className="text-right w-56 text-xs">
          <h2 className="text-2xl font-light text-gray-400 mb-2 uppercase">Invoice</h2>
          <div className="space-y-0.5">
            <div className="flex justify-between"><span>Date:</span><span>{invoice.date}</span></div>
            <div className="flex justify-between"><span>Invoice #:</span><span>{invoice.invoiceNumber}</span></div>
            <div className="flex justify-between"><span>Customer ID:</span><span>{invoice.customerId}</span></div>
            <div className="flex justify-between"><span>Payment Due:</span><span>30 Days</span></div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6 mb-6 text-xs">
        <div>
          <div className={`${primaryGreen} text-white px-2 py-1 font-bold mb-1`}>Billed To</div>
          <div className="px-2 space-y-0.5 text-gray-600">
            <div className="font-bold text-gray-800">{invoice.client.name}</div>
            <div>{invoice.client.address}</div>
            <div>{invoice.client.city} - {invoice.client.pincode}</div>
            <div>{invoice.client.phone}</div>
          </div>
        </div>

        <div>
          <div className={`${primaryGreen} text-white px-2 py-1 font-bold mb-1`}>Ship To</div>
          <div className="px-2 space-y-0.5 text-gray-600">
            <div className="font-bold text-gray-800">{invoice.client.name}</div>
            <div>{invoice.client.address}</div>
            <div>{invoice.client.city} - {invoice.client.pincode}</div>
            <div>{invoice.client.phone}</div>
          </div>
        </div>
      </div>

      <table className="w-full mb-4 border-collapse text-xs">
        <thead>
          <tr className={`${primaryGreen} text-white font-bold`}>
            <th className="py-1 px-2 text-left">Description</th>
            <th className="py-1 px-2 text-right w-24">Rate (₹)</th>
            <th className="py-1 px-2 text-center w-16">Qty</th>
            <th className="py-1 px-2 text-right w-28">Amount (₹)</th>
          </tr>
        </thead>
        <tbody>
          {calculatedItems.map((item, idx) => (
            <tr key={idx} className="border-b">
              <td className="py-1 px-2">{item.desc}</td>
              <td className="py-1 px-2 text-right">₹{item.unitCost}</td>
              <td className="py-1 px-2 text-center">{item.qty}</td>
              <td className="py-1 px-2 text-right font-medium">₹{item.amount}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="grid grid-cols-2 gap-6 text-xs">
        <div>
          <div className={`${primaryGreen} text-white px-2 py-1 font-bold mb-1`}>Notes</div>
          <div className="bg-gray-50 p-2 text-gray-500 italic h-16">
            Payment within 30 days from invoice date.
          </div>
        </div>

        <div className="space-y-0.5">
          <div className="flex justify-between"><span>Subtotal</span><span>₹{subtotal.toFixed(2)}</span></div>
          <div className="flex justify-between"><span>Discount</span><span>₹{invoice.discount.toFixed(2)}</span></div>
          <div className="flex justify-between"><span>GST ({invoice.taxRate}%)</span><span>₹{tax.toFixed(2)}</span></div>
          <div className="flex justify-between font-bold border-t pt-1">
            <span>Total</span><span>₹{total.toFixed(2)}</span>
          </div>
        </div>
      </div>

      <div className="text-center pt-4">
        <h2 className={`text-lg font-bold ${textGreen}`}>Thank you for your business!</h2>
        <p className="text-[10px] text-gray-400">
          For any invoice queries, please contact us.
        </p>
      </div>
    </div>
  );
};

export default FreelancerTemplate;
